import {
	bidSuretyBondClosedReasonSchema,
	carrierName,
	currencySchema,
	finalContractSuretyBondClosedReasonSchema,
	isoDateSchema,
	suretyBondId,
	suretyBondStatusSchema,
	suretyBondTypeSchema,
	suretyQuoteId,
	usStateOrTerritorySchema,
} from "@inrev/common";
import * as z from "zod";
import { listData, listInput } from "../schemas/fragments";
import { defineTool } from "../tool-definition";

/** Contract bonds are bid or final; the record's other bond types are never written here. */
const contractBondTypeSchema = suretyBondTypeSchema.extract(["bid", "final"]);

/**
 * No exposure figure on purpose: a row carrying one invites the model to sum the list, and a sum
 * of rows cannot know which bid bonds a bid-to-final request replaces. Totals come from
 * `derive_open_exposure`.
 */
const bondSummaryBase = {
	bondId: suretyBondId,
	number: z.string(),
	status: suretyBondStatusSchema,
	carrierName,
	penalSum: currencySchema,
	projectName: z.string().nullable(),
	projectCity: z.string().nullable(),
	projectState: usStateOrTerritorySchema.nullable(),
	obligeeNames: z.array(z.string()),
	effectiveDate: isoDateSchema.nullable(),
	expirationDate: isoDateSchema.nullable(),
	closedAt: isoDateSchema.nullable(),
	bondRequestId: suretyQuoteId.nullable(),
};

const bondSummarySchema = z.discriminatedUnion("type", [
	z.strictObject({
		...bondSummaryBase,
		type: z.literal("bid"),
		closedReason: bidSuretyBondClosedReasonSchema.nullable(),
	}),
	z.strictObject({
		...bondSummaryBase,
		type: z.literal("final"),
		contractAmount: currencySchema,
		closedReason: finalContractSuretyBondClosedReasonSchema.nullable(),
	}),
]);

export const listBonds = defineTool({
	name: "list_bonds",
	description:
		"Bonds InRev wrote on the account, newest first. `type` is `bid` or `final`. `penalSum` is the bond's amount: on a bid bond the bid it secures, on a final bond the current term's amount; a final bond also carries `contractAmount`. Closed final bonds are finished work and open ones work in progress; `closedReason` says how a bond ended. `bondRequestId` is the request it was bound from. Never add these rows up for exposure or capacity: call `derive_open_exposure`, which nets bid bonds against their bid-to-final requests the way the account page does. Project description, scope of work, completion and form detail are on `get_bond`.",
	label: "Bonds",
	contexts: ["account"],
	implementation: "api",
	audience: "model",
	input: listInput({
		status: suretyBondStatusSchema.optional(),
		type: contractBondTypeSchema.optional(),
	}),
	data: listData(bondSummarySchema),
});
